/**
 * Shared pure records and offline CSV export for the public RISSProp template.
 * This prepares files for review; it does not submit, mark reported, or certify
 * acceptance. The receiving region/store determines its own required fields
 * and weight convention. Never pass normalized/recalculated transaction data.
 */
export const RISS_HEADERS = Object.freeze([
  'Invoice #', 'Date', 'Time', 'Transaction Type', '$ Value',
  'Customer First Name', 'Customer Last Name', 'Customer Street', 'Customer City',
  'Customer State', 'Customer Zip', 'Customer DOB', 'Customer ID Number',
  'Customer ID Type', 'Item Type', 'Manufacturer', 'Model', 'Description',
  'Serial #', 'Inscription', 'Material', 'Weight', 'Item Filename',
  'Customer ID Filename', 'Customer Filename',
])

export const RISS_FIELD_OPTIONS = Object.freeze({
  'Transaction Type': Object.freeze(['Bought', 'Sold', 'Pawn', 'Trade']),
  'Customer ID Type': Object.freeze(['Drivers License', 'Immigration Card', 'Military ID', 'Passport', 'State ID']),
  'Item Type': Object.freeze(['Other', 'Appliance', 'Computer/Laptop', 'Firearm', 'Game/Toys', 'Jewelry', 'Musical Instrument', 'Phone/Tablet', 'Scrap Metal', 'Tools']),
  Material: Object.freeze(['Gold', 'Silver', 'Other - Precious Metal', 'Platinum', 'Iron', 'Aluminum', 'Catalytic Converter', 'Other - Ferrous Metal', 'Other - Non-ferrous Metal']),
})

export const RISS_WEIGHT_UNITS = Object.freeze([
  Object.freeze({ value: 'grams', label: 'Grams (g)' }),
  Object.freeze({ value: 'troy_ounces', label: 'Troy ounces (ozt)' }),
  Object.freeze({ value: 'pennyweights', label: 'Pennyweights (dwt)' }),
])

// Our conservative export checks, not a universal RISS regional requirement.
export const RISS_CORE_REQUIRED_FIELDS = Object.freeze([
  'Invoice #', 'Date', 'Time', 'Transaction Type', '$ Value',
  'Customer First Name', 'Customer Last Name', 'Customer ID Number',
  'Customer ID Type', 'Item Type', 'Description', 'Material', 'Weight',
])

export const RISS_XML_REQUIRED_FIELDS = Object.freeze(RISS_CORE_REQUIRED_FIELDS.filter(
  field => !['$ Value', 'Customer ID Number', 'Customer ID Type', 'Description'].includes(field),
))

const GRAMS_PER_UNIT = { grams: 1, troy_ounces: 31.1034768, pennyweights: 1.55517384 }
const OPTIONAL_FIELDS = RISS_HEADERS.filter(field => !RISS_CORE_REQUIRED_FIELDS.includes(field))
const JEWELRY = new Set(['ring', 'chain', 'bracelet', 'anklet', 'necklace', 'pendant', 'earring', 'earrings', 'watch', 'jewelry', 'jewellery'])
const ID_TYPES = {
  driver_license: 'Drivers License', drivers_license: 'Drivers License',
  identification_card: 'State ID', state_id: 'State ID', passport: 'Passport',
  military_id: 'Military ID', immigration_card: 'Immigration Card',
}
const MATERIAL_CODES = { au: 'Gold', ag: 'Silver', pt: 'Platinum', fe: 'Iron', al: 'Aluminum' }
const present = value => value !== null && value !== undefined && value !== ''
const textValue = value => typeof value === 'string' || typeof value === 'number' ? String(value) : ''

function validCalendarDate(value) {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false
  const [year, month, day] = value.split('-').map(Number)
  if (year < 1 || month < 1 || month > 12 || day < 1) return false
  const leap = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0)
  return day <= [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][month - 1]
}

function timestampDate(value) {
  try {
    let date
    if (value instanceof Date) date = new Date(value.getTime())
    else if (value && typeof value.toDate === 'function') date = value.toDate()
    else if (value && typeof value === 'object' && Number.isInteger(value.seconds)) {
      const nanos = value.nanoseconds ?? 0
      if (!Number.isInteger(nanos) || nanos < 0 || nanos >= 1e9) return null
      date = new Date(value.seconds * 1000 + Math.floor(nanos / 1e6))
    } else if (typeof value === 'number' && Number.isFinite(value)) date = new Date(value)
    else if (typeof value === 'string') {
      // A date without its offset is ambiguous and may change with this computer's TZ.
      const match = /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.\d{1,9})?)?(Z|[+-]\d{2}:\d{2})$/i.exec(value)
      if (!match || !validCalendarDate(match[1]) || +match[2] > 23 || +match[3] > 59 || +(match[4] || 0) > 59) return null
      if (match[5].toUpperCase() !== 'Z') {
        const [hours, minutes] = match[5].slice(1).split(':').map(Number)
        if (hours > 23 || minutes > 59) return null
      }
      date = new Date(value)
    }
    return date instanceof Date && Number.isFinite(date.getTime()) ? date : null
  } catch {
    return null
  }
}

function dateParts(date, formatter) {
  const parts = Object.fromEntries(formatter.formatToParts(date).map(part => [part.type, part.value]))
  const iso = `${parts.year.padStart(4, '0')}-${parts.month.padStart(2, '0')}-${parts.day.padStart(2, '0')}`
  return {
    iso,
    date: `${Number(parts.month)}/${Number(parts.day)}/${parts.year}`,
    time: parts.dayPeriod
      ? `${Number(parts.hour)}:${parts.minute}:${parts.second} ${parts.dayPeriod}`
      : `${parts.hour.padStart(2, '0')}:${parts.minute}:${parts.second}`,
  }
}

// Decimal half-up, not binary Math.round(value * 100); no currency symbols or
// locale guessing. BigInt is internal only: the public result is serializable.
function moneyToCents(value, alreadyCents = false) {
  if (typeof value !== 'string' && typeof value !== 'number') return null
  if (typeof value === 'number' && !Number.isFinite(value)) return null
  const source = String(value).trim()
  if (alreadyCents) {
    if (!/^\d+$/.test(source)) return null
    const result = Number(source)
    return Number.isSafeInteger(result) ? result : null
  }
  const match = /^(\d*)(?:\.(\d*))?(?:[eE]([+-]?\d+))?$/.exec(source)
  if (!match || !(match[1] || match[2])) return null
  const exponent = Number(match[3] || 0)
  if (!Number.isSafeInteger(exponent) || Math.abs(exponent) > 100 || source.length > 200) return null
  let cents = BigInt((match[1] || '0') + (match[2] || ''))
  const shift = exponent - (match[2] || '').length + 2
  if (shift >= 0) cents *= 10n ** BigInt(shift)
  else {
    const divisor = 10n ** BigInt(-shift)
    cents = (cents + divisor / 2n) / divisor
  }
  return cents <= BigInt(Number.MAX_SAFE_INTEGER) ? Number(cents) : null
}

function centsText(cents) {
  return `${Math.floor(cents / 100)}.${String(cents % 100).padStart(2, '0')}`
}

function normalizedOption(value, field) {
  if (typeof value !== 'string') return ''
  return RISS_FIELD_OPTIONS[field].find(option => option.toLowerCase() === value.trim().toLowerCase()) || ''
}

// Only recorded decimal numbers describe material/purity. JavaScript coercion
// must not turn booleans, arrays, hexadecimal strings or blanks into gold/karats.
function recordedNumber(value) {
  if (typeof value === 'number') return Number.isFinite(value) ? value : NaN
  if (typeof value !== 'string' || !/^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?$/i.test(value.trim())) return NaN
  const number = Number(value.trim())
  return Number.isFinite(number) ? number : NaN
}

function itemMaterial(item) {
  if (present(item.metal)) {
    return normalizedOption(item.metal, 'Material') || MATERIAL_CODES[String(item.metal).trim().toLowerCase()] || ''
  }
  if (present(item.material)) return normalizedOption(item.material, 'Material')
  const karat = recordedNumber(item.karat)
  if (karat === 0) return 'Silver'
  if (karat > 0 && karat <= 24) return 'Gold'
  return ''
}

function itemCategory(item) {
  const value = textValue(item.itemType).trim()
  const lower = value.toLowerCase()
  if (JEWELRY.has(lower)) return { value: 'Jewelry', needsReview: false }
  if (lower === 'scrap' || lower === 'scrap_metal') return { value: 'Scrap Metal', needsReview: false }
  if (['coin', 'bar', 'other'].includes(lower)) return { value: 'Other', needsReview: false }
  const official = normalizedOption(value, 'Item Type')
  return { value: official || 'Other', needsReview: !official }
}

function descriptionFor(item, material, grams) {
  const description = textValue(item.description)
  if (description.trim()) return description
  const parts = []
  if (material) parts.push(material)
  if (material === 'Gold') {
    let karat = null
    if (present(item.karatValue)) karat = recordedNumber(item.karatValue)
    else if (present(item.karat)) karat = recordedNumber(item.karat)
    else if (present(item.karatWhole)) {
      const decimal = present(item.karatDecimal) ? recordedNumber(item.karatDecimal) : 0
      karat = recordedNumber(item.karatWhole) + decimal / 100
    }
    if (Number.isFinite(karat) && karat > 0 && karat <= 24) parts.push(`${karat}K`)
  }
  if (Number.isFinite(grams) && grams > 0) parts.push(`${grams} g`)
  if (textValue(item.itemType).trim()) parts.push(textValue(item.itemType))
  return parts.join(' · ')
}

function filenameIsSafe(value) {
  // Filenames must name files included separately by the operator. Never export
  // storage download URLs, tokens, absolute paths, or directory traversal.
  return typeof value === 'string' && value.trim() === value && value.length > 0 &&
    value !== '.' && value !== '..' && !/[\\/:?#\u0000-\u001f\u007f]/.test(value) &&
    !/^[\s]*[=+@-]/u.test(value) && !/^(?:https?|data|gs):/i.test(value)
}

function unsafeSpreadsheetText(value) {
  return /^[\s\uFEFF]*[=+@-]/u.test(value) || /^[\t\r\n]/.test(value)
}

function csvCell(value) {
  const string = String(value)
  return /[",\r\n]/.test(string) ? `"${string.replaceAll('"', '""')}"` : string
}

function csvFor(rows) {
  return [RISS_HEADERS, ...rows].map(row => row.map(csvCell).join(',')).join('\r\n') + '\r\n'
}

/**
 * @param {Array<object>} transactions Raw records with their actual document id.
 * @param {{startDate:string,endDate:string,timeZone?:string,weightUnit:string,
 * requiredFields?:string[],attachmentFilenames?:Object}} options
 * @returns {{headers:string[],rows:string[][],errors:object[],warnings:object[],
 * batches:object[],eligibleTransactionIds:string[],excludedTransactionIds:string[],
 * rowCount:number,transactionCount:number}}
 */
export function buildRissExport(transactions, options = {}) {
  return buildRissRecords(transactions, options)
}

/** XML consumes these validated records directly, not reparsed CSV rows. */
export function buildRissRecords(transactions, options = {}, { profile = 'csv' } = {}) {
  const xmlProfile = profile === 'xml'
  const errors = []
  const warnings = []
  const rows = []
  const rowDetails = []
  const eligibleTransactionIds = []
  const excludedTransactionIds = []
  const reviewTransactionIds = []
  const batches = []
  const records = []
  const issue = (list, code, message, context = {}) => list.push({ code, message, ...context })
  const result = () => ({
    headers: [...RISS_HEADERS], rows, rowDetails, errors, warnings, batches,
    eligibleTransactionIds, excludedTransactionIds, reviewTransactionIds,
    rowCount: rows.length, transactionCount: eligibleTransactionIds.length,
    ...(xmlProfile ? { records } : {}),
  })

  if (!options || typeof options !== 'object') options = {}
  const { startDate, endDate, weightUnit, timeZone = 'America/New_York' } = options
  if (!validCalendarDate(startDate) || !validCalendarDate(endDate) || startDate > endDate) {
    issue(errors, 'INVALID_DATE_RANGE', 'Choose a valid inclusive start/end date in YYYY-MM-DD format.')
  }
  if (!Object.hasOwn(GRAMS_PER_UNIT, weightUnit)) {
    issue(errors, 'WEIGHT_UNIT_REQUIRED', 'Confirm the weight unit required by your receiving RISS region/store before exporting.')
  }
  const required = new Set(xmlProfile ? RISS_XML_REQUIRED_FIELDS : RISS_CORE_REQUIRED_FIELDS)
  const optionalFields = xmlProfile ? RISS_HEADERS.filter(field => !required.has(field)) : OPTIONAL_FIELDS
  if (options.requiredFields !== undefined && !Array.isArray(options.requiredFields)) {
    issue(errors, 'INVALID_REQUIRED_FIELDS', 'Required fields must be a list of official RISS column names.')
  } else {
    for (const field of options.requiredFields || []) {
      if (!RISS_HEADERS.includes(field)) issue(errors, 'UNKNOWN_REQUIRED_FIELD', `Unknown RISS required field: ${String(field)}.`)
      else required.add(field)
    }
  }
  let formatter
  try {
    if (typeof timeZone !== 'string' || !timeZone) throw new Error('Missing time zone')
    formatter = new Intl.DateTimeFormat('en-US-u-ca-gregory-nu-latn', {
      timeZone, year: 'numeric', month: '2-digit', day: '2-digit',
      hour: 'numeric', minute: '2-digit', second: '2-digit',
      ...(xmlProfile ? { hourCycle: 'h23' } : { hour12: true }),
    })
  } catch {
    issue(errors, 'INVALID_TIME_ZONE', 'Choose a valid IANA time zone for the transaction date and time.')
  }
  if (!Array.isArray(transactions)) issue(errors, 'INVALID_TRANSACTIONS', 'Transactions must be an array of raw purchase records.')
  if (errors.length) return result()

  const seenIds = new Set()
  const selected = []
  for (const tx of transactions) {
    const transactionId = textValue(tx?.id)
    const context = { transactionId }
    const date = timestampDate(tx?.createdAt)
    if (!date) {
      issue(errors, 'INVALID_TRANSACTION_DATE', 'The original transaction timestamp is missing, invalid, or lacks a time zone; its inclusion in this date range cannot be verified.', { ...context, field: 'Date' })
      excludedTransactionIds.push(transactionId)
      reviewTransactionIds.push(transactionId)
      continue
    }
    const local = dateParts(date, formatter)
    if (local.iso < startDate || local.iso > endDate) {
      excludedTransactionIds.push(transactionId)
      continue
    }
    if (!transactionId.trim() || seenIds.has(transactionId)) {
      issue(errors, transactionId.trim() ? 'DUPLICATE_TRANSACTION_ID' : 'MISSING_TRANSACTION_ID', 'Each purchase must have its original, unique transaction document ID.', context)
      excludedTransactionIds.push(transactionId)
      reviewTransactionIds.push(transactionId)
      continue
    }
    seenIds.add(transactionId)
    if (tx.legacyPoliceReport != null) {
      issue(errors, 'LEGACY_LOCAL_POLICE_REPORT', 'This purchase is recorded as reported to local police outside this system. Exclude it from the new RISS submission; legacy reports are not resent automatically.', context)
      excludedTransactionIds.push(transactionId)
      reviewTransactionIds.push(transactionId)
      continue
    }
    const status = textValue(tx.status).trim().toLowerCase()
    if (['cancelled', 'canceled', 'deleted', 'void', 'voided'].includes(status) || tx.cancelledAt || tx.canceledAt || tx.deletedAt || tx.isDeleted === true || tx.deleted === true || tx.softDeleted === true) {
      issue(errors, 'TRANSACTION_REQUIRES_REVIEW', 'This purchase is cancelled, voided, or deleted. Review it and explicitly exclude it or resolve its status before preparing an export; it was not silently omitted.', context)
      excludedTransactionIds.push(transactionId)
      reviewTransactionIds.push(transactionId)
      continue
    }
    if (status && status !== 'completed') {
      issue(errors, 'UNEXPECTED_TRANSACTION_STATUS', 'The purchase is not marked completed. Review its actual status before exporting it as Bought.', context)
      excludedTransactionIds.push(transactionId)
      reviewTransactionIds.push(transactionId)
      continue
    }
    if (!status) issue(warnings, 'LEGACY_STATUS_MISSING', 'This legacy purchase has no stored status. Confirm that it is a completed purchase before finalizing the upload.', context)
    selected.push({ tx, transactionId, date, local })
  }
  selected.sort((a, b) => a.date - b.date || (a.transactionId < b.transactionId ? -1 : a.transactionId > b.transactionId ? 1 : 0))

  for (const { tx, transactionId, local } of selected) {
    const context = { transactionId }
    const beforeErrors = errors.length
    const customer = tx.customer && typeof tx.customer === 'object' ? tx.customer : {}
    if (xmlProfile) for (const key of ['phone', 'idState', 'idExpiration']) {
      const value = customer[key]
      if (present(value) && ((typeof value !== 'string' && typeof value !== 'number') ||
          (typeof value === 'number' && !Number.isSafeInteger(value)))) {
        issue(errors, 'INVALID_XML_CUSTOMER_FIELD', `The supplied customer ${key} must be a valid scalar value.`, { ...context, field: key })
      }
    }
    const items = Array.isArray(tx.items) ? tx.items : []
    if (!items.length) issue(errors, 'MISSING_ITEMS', 'The purchase has no stored items to export.', context)
    const readAmount = (candidates, amountContext) => {
      const values = []
      for (const [value, cents, source] of candidates) {
        if (!present(value)) continue
        const parsed = moneyToCents(value, cents)
        if (parsed === null) issue(errors, 'INVALID_AMOUNT', `Stored ${source} is not a valid non-negative monetary amount.`, { ...amountContext, field: '$ Value' })
        else values.push(parsed)
      }
      if (new Set(values).size > 1) issue(errors, 'CONFLICTING_AMOUNTS', 'Stored monetary fields disagree at cent precision; review the original purchase.', { ...amountContext, field: '$ Value' })
      return values[0] ?? null
    }
    const totalCents = readAmount([
      [tx.totals?.payoutCents, true, 'totals.payoutCents'], [tx.payoutCents, true, 'payoutCents'],
      [tx.totals?.totalNet, false, 'totals.totalNet'], [tx.totalNet, false, 'totalNet'],
      [tx.totalPayout, false, 'totalPayout'],
    ], context)
    if (totalCents === null) issue(errors, 'MISSING_TRANSACTION_TOTAL', 'The stored final purchase total is required to reconcile item values; it will not be reconstructed from spot prices.', { ...context, field: '$ Value' })

    let dob = ''
    if (present(customer.dateOfBirth)) {
      if (validCalendarDate(customer.dateOfBirth)) {
        const [year, month, day] = customer.dateOfBirth.split('-')
        if (customer.dateOfBirth > local.iso) issue(errors, 'FUTURE_CUSTOMER_DOB', 'Customer date of birth is later than the purchase date. Review the original customer record.', { ...context, field: 'Customer DOB' })
        else dob = `${Number(month)}/${Number(day)}/${year}`
      } else issue(errors, 'INVALID_CUSTOMER_DOB', 'Customer date of birth must be a real calendar date stored as YYYY-MM-DD.', { ...context, field: 'Customer DOB' })
    }
    const idType = ID_TYPES[textValue(customer.idType).trim().toLowerCase()] || normalizedOption(customer.idType, 'Customer ID Type')
    // The app's legacy "other" means the actual document category is unknown,
    // not a new RISS ID category. XML permits its omission; CSV stays strict.
    const omitXmlIdType = xmlProfile && typeof customer.idType === 'string' && customer.idType.trim().toLowerCase() === 'other'
    if (omitXmlIdType) issue(warnings, 'XML_ID_TYPE_OMITTED', 'The original ID type is recorded as Other. Its optional XML element is omitted without changing the original ID type or number; confirm any additional receiving requirements.', { ...context, field: 'Customer ID Type' })
    else if (present(customer.idType) && !idType) issue(errors, 'UNSUPPORTED_ID_TYPE', 'The stored customer ID type does not match an official RISS template option.', { ...context, field: 'Customer ID Type' })
    if (xmlProfile && present(customer.idNumber) && (!textValue(customer.idNumber).trim() ||
        (typeof customer.idNumber === 'number' && (!Number.isSafeInteger(customer.idNumber) || customer.idNumber < 0)))) {
      issue(errors, 'INVALID_CUSTOMER_ID_NUMBER', 'The supplied customer ID number is invalid; an absent ID is allowed, but a supplied value must be valid.', { ...context, field: 'Customer ID Number' })
    }
    const filenames = options.attachmentFilenames?.[transactionId] || {}
    const transactionRows = []
    const transactionRecords = []
    let itemSum = 0
    let allAmountsKnown = true

    items.forEach((rawItem, itemIndex) => {
      const item = rawItem && typeof rawItem === 'object' ? rawItem : {}
      const itemContext = { transactionId, itemIndex }
      let cents = readAmount([
        [item.payoutCents, true, 'item.payoutCents'],
        [item.netValue, false, 'item.netValue'], [item.subtotal, false, 'item.subtotal'],
      ], itemContext)
      if (cents === null && items.length === 1 && totalCents !== null) {
        cents = totalCents
        issue(warnings, 'SINGLE_ITEM_TOTAL_USED', 'This single-item purchase uses its stored final purchase total as the item value.', { ...itemContext, field: '$ Value' })
      }
      if (cents === null) {
        allAmountsKnown = false
        if (xmlProfile && !required.has('$ Value')) {
          if (![item.payoutCents, item.netValue, item.subtotal].some(present)) {
            issue(warnings, 'XML_ITEM_AMOUNT_OMITTED', 'This item has no recorded individual payout. Its optional TransactionAmount element is omitted; the purchase total is not split, repeated, or recalculated. Confirm any additional receiving requirements.', { ...itemContext, field: '$ Value' })
          }
        } else issue(errors, 'MISSING_ITEM_AMOUNT', 'An item has no stored final paid value. Multi-item purchases require review; values will not be split or recalculated from spot prices.', { ...itemContext, field: '$ Value' })
      } else itemSum += cents

      const weightSource = item.weightGrams ?? item.weight
      const grams = typeof weightSource === 'number' || (typeof weightSource === 'string' && weightSource.trim()) ? Number(weightSource) : NaN
      const converted = grams / GRAMS_PER_UNIT[weightUnit]
      let weight = ''
      if (!Number.isFinite(grams) || grams <= 0 || !Number.isFinite(converted)) {
        issue(errors, 'INVALID_ITEM_WEIGHT', 'Each item must have its actual positive weight stored in grams; missing or invalid weight will not be inferred.', { ...itemContext, field: 'Weight' })
      } else {
        weight = converted.toFixed(8).replace(/\.?0+$/, '')
        if (Number(weight) <= 0 || /e/i.test(weight)) issue(errors, 'UNREPRESENTABLE_WEIGHT', 'The item weight cannot be represented safely with eight decimal places in the selected unit.', { ...itemContext, field: 'Weight' })
      }
      const material = itemMaterial(item)
      if (!material) issue(errors, 'UNKNOWN_MATERIAL', 'The stored metal/material is missing or is not an official RISS template option.', { ...itemContext, field: 'Material' })
      const category = itemCategory(item)
      if (category.needsReview) issue(xmlProfile ? errors : warnings, xmlProfile ? 'INVALID_XML_ITEM_TYPE' : 'ITEM_TYPE_REVIEW', xmlProfile
        ? 'XML requires a recorded, recognized item type. Select Other explicitly only when it describes the original item; an absent or unknown type will not be inferred.'
        : 'The item type is missing or unmapped, so Other is used. Review the classification and its factual description.', { ...itemContext, field: 'Item Type' })
      // Preserve an entered description; otherwise use the same recorded facts
      // as CSV. XML still requires an explicitly recorded, recognized item type.
      const canDescribe = !xmlProfile || !category.needsReview
      const description = canDescribe ? descriptionFor(item, material, grams) : textValue(item.description)
      if (xmlProfile && ![description, textValue(item.model), textValue(item.manufacturer)].some(value => value.trim())) {
        issue(errors, 'MISSING_ITEM_IDENTIFICATION', 'The item has no recorded identification and a description could not be generated from its saved details. Review the item.', { ...itemContext, field: 'Description' })
      }
      if (canDescribe && !textValue(item.description).trim()) issue(warnings, 'GENERATED_DESCRIPTION', 'Description uses only the recorded metal, karat, weight, and item type. Review whether additional identifying details are required.', { ...itemContext, field: 'Description' })
      const values = {
        'Invoice #': textValue(tx.transactionNumber), Date: local.date, Time: local.time,
        'Transaction Type': 'Bought', '$ Value': cents === null ? '' : centsText(cents),
        'Customer First Name': textValue(customer.firstName), 'Customer Last Name': textValue(customer.lastName),
        'Customer Street': textValue(customer.street), 'Customer City': textValue(customer.city),
        'Customer State': textValue(customer.state), 'Customer Zip': textValue(customer.zip),
        'Customer DOB': dob, 'Customer ID Number': textValue(customer.idNumber), 'Customer ID Type': idType,
        'Item Type': category.value, Manufacturer: textValue(item.manufacturer), Model: textValue(item.model),
        Description: description, 'Serial #': textValue(item.serialNumber), Inscription: textValue(item.inscription),
        Material: material, Weight: weight,
        'Item Filename': '', 'Customer ID Filename': '', 'Customer Filename': '',
      }
      const imageFields = [
        ['Item Filename', filenames.item?.[itemIndex]],
        ['Customer ID Filename', filenames.customerId], ['Customer Filename', filenames.customer],
      ]
      for (const [field, filename] of imageFields) {
        if (present(filename)) {
          if (!filenameIsSafe(filename)) issue(errors, 'INVALID_ATTACHMENT_FILENAME', 'Use only the filename of an image supplied with this batch, never a URL, token, or file-system path.', { ...itemContext, field })
          else values[field] = filename
        }
      }
      for (const field of RISS_HEADERS) {
        if (!values[field].trim()) {
          if (required.has(field)) issue(errors, 'MISSING_REQUIRED_FIELD', `A field required by these export checks is missing: ${field}.`, { ...itemContext, field })
          else if (optionalFields.includes(field)) issue(warnings, field.endsWith('Filename') ? 'IMAGE_OMITTED' : 'OPTIONAL_FIELD_MISSING', field.endsWith('Filename')
            ? `${field} is omitted. Private image URLs are never exported; provide a real attachment filename if your receiving region requires this image.`
            : `${field} is not stored. It remains blank; confirm whether your receiving region requires it.`, { ...itemContext, field })
        } else if (unsafeSpreadsheetText(values[field])) {
          issue(errors, 'UNSAFE_SPREADSHEET_TEXT', `${field} starts with a spreadsheet formula/control character. Review the source value; it has not been silently changed or prefixed.`, { ...itemContext, field })
        } else if (/[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/.test(values[field])) {
          issue(errors, 'UNSAFE_CONTROL_CHARACTER', `${field} contains an unsupported control character. Review the source value.`, { ...itemContext, field })
        }
      }
      transactionRows.push(RISS_HEADERS.map(field => values[field]))
      if (xmlProfile) transactionRecords.push({
        transactionId, itemIndex, itemId: textValue(item.itemId), fields: values, omitXmlIdType,
        customer: { phone: textValue(customer.phone), idState: textValue(customer.idState), idExpiration: textValue(customer.idExpiration) },
      })
    })

    if (allAmountsKnown && (!Number.isSafeInteger(itemSum) || (totalCents !== null && itemSum !== totalCents))) {
      issue(errors, 'ITEM_TOTAL_MISMATCH', `Stored item values (${Number.isSafeInteger(itemSum) ? centsText(itemSum) : 'out of range'}) do not match the stored final purchase total (${totalCents === null ? 'missing' : centsText(totalCents)}). No amounts were redistributed.`, { ...context, field: '$ Value' })
    } else if (xmlProfile && !allAmountsKnown && (!Number.isSafeInteger(itemSum) || (totalCents !== null && itemSum > totalCents))) {
      issue(errors, 'ITEM_TOTAL_MISMATCH', 'The known item values exceed the recorded final purchase total or the safe amount range. Missing item values cannot resolve this conflict; no amounts were redistributed.', { ...context, field: '$ Value' })
    }
    if (errors.length === beforeErrors) {
      rows.push(...transactionRows)
      transactionRows.forEach((_, itemIndex) => rowDetails.push({ transactionId, itemIndex, itemId: textValue(items[itemIndex]?.itemId) }))
      eligibleTransactionIds.push(transactionId)
      if (xmlProfile) records.push(...transactionRecords)
    } else {
      excludedTransactionIds.push(transactionId)
      reviewTransactionIds.push(transactionId)
    }
  }

  issue(warnings, 'RECEIVING_REQUIREMENTS', 'This export is not confirmation of RISS acceptance. Verify your receiving region/store required fields, weight unit, field mapping, attachments, and validation before finalizing an upload.')
  if (!xmlProfile && rows.length > 1000) issue(warnings, 'SEPARATE_BATCH_UPLOADS', 'Each CSV contains at most 1,000 item records. Upload each batch separately: the RISS limit is 1,000 records total across all files in one upload.')
  if (!rows.length && !errors.length) issue(warnings, 'NO_TRANSACTIONS', 'No purchases fall within the selected dates in the selected time zone.')
  if (!errors.length && !xmlProfile) {
    for (let offset = 0; offset < rows.length; offset += 1000) {
      const batchRows = rows.slice(offset, offset + 1000)
      batches.push({
        filename: `RISSProp_${startDate}_${endDate}_${String(batches.length + 1).padStart(3, '0')}.csv`,
        csv: csvFor(batchRows), rowCount: batchRows.length,
        transactionIds: [...new Set(rowDetails.slice(offset, offset + 1000).map(detail => detail.transactionId))],
      })
    }
  }
  return result()
}

export function formatRissTimestamp(value, timeZone = 'America/New_York') {
  const date = timestampDate(value)
  if (!date) return null
  try {
    return dateParts(date, new Intl.DateTimeFormat('en-US-u-ca-gregory-nu-latn', {
      timeZone, year: 'numeric', month: '2-digit', day: '2-digit',
      hour: 'numeric', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
    }))
  } catch { return null }
}

export function formatRissCalendarDate(value) {
  if (!validCalendarDate(value)) return null
  const [year, month, day] = value.split('-')
  return `${Number(month)}/${Number(day)}/${year}`
}
